import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { Box, Grid, makeStyles, Theme, Typography } from "@material-ui/core";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import { FaqDashIcon, FaqPlusIcon } from "assets/svgs";
import clsx from "clsx";
import { NextRouter, useRouter } from "next/router";

interface AccordionContent {
  title: string;
  description: string;
  icon?: any;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      width: '100%',
    },
    accordionItem: {
      backgroundColor: 'inherit',
      border: '1px solid rgba(151, 151, 151, 0.4)',
      borderRadius: '15px !important',
      color: 'inherit',
      marginTop: '20px !important',
      position: 'relative',
      cursor: 'pointer',
      boxShadow: 'none',
      '& .MuiIconButton-root': {
        color: 'transparent !important',
      },
    },
    accordionSummary: {
      padding: theme.spacing(2, 3),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4, 5),
      },
    },
    heading: {
      fontSize: 18,
      fontWeight: 600,
      color: theme.palette.secondary.main,
      [theme.breakpoints.up('sm')]: {
        fontSize: 20,
      },
    },
    description: {
      fontSize: 18,
      fontWeight: 400,
      lineHeight: 1.8,
      color: theme.palette.customColors.main,
      padding: theme.spacing(0, 5, 6),
      marginTop: -10,
    },
    markText: {
      animation: '$markHighlight 1.2s ease-in-out forwards',
      padding: theme.spacing(1),
      borderRadius: 3,
    },
    hiddenElement: {
      position: 'absolute',
      top: '-200px',
    },
    '@keyframes markHighlight': {
      from: { backgroundColor: theme.palette.primary.main },
      to: { backgroundColor: 'transparent' },
    },
  };
});

const ToggleAccordion: FC<AccordionContent> = ({ title, description, icon }) => {
  const classes = useStyles({ title, description, icon });
  const [expanded, setExpanded] = useState<boolean>(false);
  const { asPath }: NextRouter = useRouter();
  const currentId = asPath.split('#')[1];
  const handleChange = (event: ChangeEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };
  const accordionId = title.split(' ').join('-');

  useEffect(() => {
    if (currentId === accordionId) {
      setExpanded(true);
    }
  }, [currentId]);

  return (
    <Box position={'relative'}>
      <Box id={accordionId} className={classes.hiddenElement} />
      <Box className={classes.root}>
        <Accordion className={classes.accordionItem} expanded={expanded} onChange={handleChange}>
          <AccordionSummary
            expandIcon={expanded ? <FaqDashIcon /> : <FaqPlusIcon />}
            aria-controls="panel-content"
            id="panel-header"
            className={classes.accordionSummary}
          >
            <Typography component={'span'} className={classes.heading}>
              <Box className={clsx(currentId === accordionId && classes.markText)}>{title}</Box>
            </Typography>
          </AccordionSummary>
          <Grid xs={12} sm={12} md={11} lg={11} item>
            <div
              className={classes.description}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </Grid>
        </Accordion>
      </Box>
    </Box>
  );
};

export default ToggleAccordion;
